import {
  ErrorFallback,
  LoadingScreen,
  OfflineFallBack,
} from "@/components/misc";
import { ThemeProvider } from "@/context/Theme";
import "@/styles/main.scss";
import { QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, theme } from "antd";
import { ErrorInfo, lazy, Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { registerSW } from "virtual:pwa-register";
import { ReloadOutlined } from "./assets/Icon";
import { AppButton } from "./components";
import useUtilStore from "./data/util";
import { ThemeEnum } from "./enums";
import { useEffectOnce, useOnlineStatus } from "./hooks";
import AppRoutes from "./routes";
import { IRoute, ISubRoute } from "./types";
import { AppNotification } from "./utils";
import { appName } from "./utils/Constant";
import AppLogger from "./utils/Logger";
import { queryClient } from "./utils/queryClient";

const NotFound = lazy(() => import("@/components/misc/NotFound"));

function App() {
  const { theme: storeTheme } = useUtilStore();
  const isDarkTheme = storeTheme === ThemeEnum.DARK;

  useEffectOnce(() => {
    const updateSW = registerSW({
      onNeedRefresh() {
        AppNotification.success({
          desc: (
            <div className="flex_center flex-col">
              <p className="text-center">
                There is a new feature on
                <br />
                <strong>{appName}!</strong>
              </p>
              <AppButton
                className="mt-3 p-6 !rounded-none bg-nphcda hover:bg-nphcda text-white"
                onClick={() => updateSW()}
              >
                <ReloadOutlined />
                Click to Refresh
              </AppButton>
            </div>
          ),
          placement: "bottom",
          duration: 0,
        });
      },
    });
  });

  // ====================== THEME
  const _theme = useMemo(() => {
    return {
      // Use Ant Design's `darkAlgorithm` for dark mode if available
      algorithm: isDarkTheme ? theme.darkAlgorithm : theme.defaultAlgorithm,
      // Customize your theme colors here
      token: isDarkTheme
        ? {
            colorPrimary: "#007D53", // Example: Your primary color for dark theme
            colorBgContainer: "#141414", // Container background color for dark theme
            // Add more custom tokens as needed
            zIndexPopupBase: 1100,
          }
        : {
            // Define light theme colors if needed
            colorPrimary: "#007D53",
            zIndexPopupBase: 1100,
          },
    };
  }, [isDarkTheme]);

  /**
   * @description This function renders the routes
   * @param {IRoute[]}
   */

  const handleError = (error: Error, info: ErrorInfo) => {
    if (!info.componentStack) return;
    const _error = info?.componentStack.split("\n")[1];
    AppLogger.error(
      `Error: ${error.message} \n Stack: ${_error} \n Info: ${info.componentStack}`
    );
  };

  const renderRoutes = (routes: IRoute[]) =>
    routes.map((route: IRoute, i: number) => (
      <Route key={route.path} path={route.path}>
        <Route key={`${route.path}_${i}`} index element={<route.element />} />
        {route?.sub?.map?.((sub: ISubRoute, j: number) => (
          <Route
            key={`${sub.path}_${j}`}
            path={sub.path}
            element={
              <Suspense fallback={<LoadingScreen />}>
                <ErrorBoundary
                  FallbackComponent={ErrorFallback}
                  onError={handleError}
                >
                  <sub.subElement />
                </ErrorBoundary>
              </Suspense>
            }
          />
        ))}
      </Route>
    ));

  const isOnline = useOnlineStatus();

  return (
    <ConfigProvider theme={_theme}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          {isOnline ? (
            <Suspense fallback={<LoadingScreen />}>
              {/* <Router basename="/phc/"> */}
              <Router>
                <Routes>
                  {renderRoutes(AppRoutes)}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Router>
            </Suspense>
          ) : (
            <OfflineFallBack />
          )}
        </ThemeProvider>
      </QueryClientProvider>
    </ConfigProvider>
  );
}

export default App;
